import { useMemo } from 'react';
import { TmsRouteTypeEnum, TmsCarrierTypeEnum } from '../../../enums/TmsEnum';
import { TmsCarrierType } from '../route/graphql/queries/getTmsCarriers';

/**
 * 根据 routeType 过滤 carriers 的 Hook
 * @param carriersData - 承运商数据数组
 * @param routeType - 路线类型（可选，可以是 number 或 string）
 * @returns 过滤后的 carrierOptions 数组，格式为 { label: string, value: string }[]
 */
export const useFilteredCarriers = (
  carriersData: TmsCarrierType[] | undefined,
  routeType?: number | string | null,
): { label: string; value: string }[] => {
  return useMemo(() => {
    if (!carriersData) return [];

    let filteredCarriers = carriersData;

    const routeTypeNum = routeType ? Number(routeType) : undefined;

    if (routeTypeNum === TmsRouteTypeEnum.Linehaul) {
      filteredCarriers = filteredCarriers.filter(
        carrier =>
          carrier.carrierType === TmsCarrierTypeEnum.Linehaul ||
          carrier.carrierType === TmsCarrierTypeEnum.LinehaulAndFeeder
      );
    } else if (routeTypeNum === TmsRouteTypeEnum.Feeder) {
      filteredCarriers = filteredCarriers.filter(
        carrier =>
          carrier.carrierType === TmsCarrierTypeEnum.Feeder ||
          carrier.carrierType === TmsCarrierTypeEnum.LinehaulAndFeeder
      );
    }
    // carrierSourceType 过滤已在交接方需求中废弃，不再按内部/三方过滤
    return filteredCarriers.map(carrier => ({
      label: carrier.carrierName,
      value: carrier.id
    }));
  }, [carriersData, routeType]);
};

/**
 * 检查某个 carrierId 是否对指定的 routeType 有效
 * @param carriersData - 承运商数据数组
 * @param carrierId - 要检查的承运商 ID
 * @param routeType - 路线类型（可选，可以是 number 或 string）
 * @returns 如果 carrierId 对 routeType 有效则返回 true，否则返回 false
 */
export const isCarrierValidForRouteType = (
  carriersData: TmsCarrierType[] | undefined,
  carrierId: string | null | undefined,
  routeType?: number | string | null,
): boolean => {
  if (!carriersData || !carrierId) return false;

  const routeTypeNum = routeType ? Number(routeType) : undefined;
  const carrier = carriersData.find(c => c.id === carrierId);

  if (!carrier) return false;

  // carrierSourceType 校验已在交接方需求中废弃

  if (routeTypeNum === undefined) return true;

  // Type Linehaul (干线): carrierType must be Linehaul or LinehaulAndFeeder
  if (routeTypeNum === TmsRouteTypeEnum.Linehaul) {
    return (
      carrier.carrierType === TmsCarrierTypeEnum.Linehaul ||
      carrier.carrierType === TmsCarrierTypeEnum.LinehaulAndFeeder
    );
  }

  // Type Feeder (支线): carrierType must be Feeder or LinehaulAndFeeder
  if (routeTypeNum === TmsRouteTypeEnum.Feeder) {
    return (
      carrier.carrierType === TmsCarrierTypeEnum.Feeder ||
      carrier.carrierType === TmsCarrierTypeEnum.LinehaulAndFeeder
    );
  }

  // Type ALL or other: 认为有效
  return true;
};

